import { useMutation } from "@tanstack/react-query";

import { toast } from "@/components/toast/toast-queue";

interface UseBulkCopyLinksOptions {
  onError?: (error: Error) => void;
  onSuccess?: (data: { count: number }) => void;
}

/**
 * Hook for copying multiple request links to clipboard
 * Generates full URLs for each request and copies them as line-separated text
 */
export function useBulkCopyLinks(options: UseBulkCopyLinksOptions = {}) {
  return useMutation({
    mutationFn: async (requestIds: number[]) => {
      const baseUrl = window.location.origin;
      const links = requestIds.map((id) => `${baseUrl}/requests/${id}/edit`);
      const linksText = links.join("\n");

      await navigator.clipboard.writeText(linksText);

      return { count: links.length };
    },
    onError: (error: Error) => {
      toast.add({
        description: "Failed to copy links to clipboard. Please try again.",
        timeout: 3000,
        title: "Copy Failed",
        type: "error",
      });
      options.onError?.(error);
    },
    onSuccess: (data) => {
      toast.add({
        description: `${data.count} request link${data.count === 1 ? "" : "s"} copied to clipboard`,
        timeout: 2000,
        title: "Links Copied",
        type: "success",
      });
      options.onSuccess?.(data);
    },
  });
}
